import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/roboto-mono/400.css";
import "./index.css";

// Initialize Smart Asset schemas from markdown files (must be early, before components render)
import "./initSchemas";

import { isElectron } from "./utils/runtime";
import { useIntroVideo } from "./hooks/useIntroVideo";
import { ServerConnectionProvider } from "./context/ServerConnectionContext";
import { ServerConnectionGate } from "./components/ServerConnectionGate";
import { AppShell } from "./components/layout/AppShell";
import { WalletLockScreen } from "./components/common";
import { navigationGroups, navigationViewFor, type AppView } from "./navigation";
import { useAppliedTheme } from "./theme";
import { siteMetadata } from "./siteMetadata";
import StartupVideo from "./components/StartupVideo";
import { deleteServerWallets } from "./pages/settings/serverWallets";

import {
  ASSET_PATHS,
  BITCOIN_NETWORK,
  NetworkStatus,
  RgbLibApiClient,
  useSatchelWallet,
  SatchelWalletProvider,
  WALLET_STORAGE_KEYS,
  getMockSmartAssets,
  getComposites,
  type BitcoinNetwork,
  type ChainConfig,
  type SmartAssetData,
} from "@satchel/core";

const WalletHome = lazy(() => import("@satchel/core").then((m) => ({ default: m.WalletHome })));
const SmartAssetsHome = lazy(() =>
  import("@satchel/core").then((m) => ({ default: m.SmartAssetsHome })),
);
const Dashboard = lazy(() => import("@satchel/core").then((m) => ({ default: m.Dashboard })));
const SmartAssetMasterContainer = lazy(() =>
  import("@satchel/core").then((m) => ({ default: m.SmartAssetMasterContainer })),
);
const OnboardingPage = lazy(() =>
  import("./pages/onboarding").then((m) => ({ default: m.OnboardingPage })),
);
const SettingsPage = lazy(() =>
  import("./pages/Settings").then((m) => ({ default: m.SettingsPage })),
);
const ExchangeView = lazy(() => import("./prototypes/ExchangeView"));
const Credits = lazy(() => import("./components/satchel").then((m) => ({ default: m.Credits })));
const BuyMarketplacePage = lazy(() =>
  import("./pages/BuyMarketplace").then((m) => ({ default: m.BuyMarketplacePage })),
);
const SchedulingPage = lazy(() => import("./pages/Scheduling"));

function ViewLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-pulse flex flex-col items-center">
        <img src={ASSET_PATHS.SATCHEL_LOGO} alt="" className="h-16 w-16 mb-2" />
        <span className="text-sm text-sa-text-muted">Loading...</span>
      </div>
    </div>
  );
}

function isBuyMarketplaceRoute(): boolean {
  return window.location.pathname === "/satchel/buy" || window.location.hash === "#/satchel/buy";
}

function App() {
  useAppliedTheme();

  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(
    () => localStorage.getItem("satchel_onboarding_completed") === "true",
  );
  // Transition state to ensure clean unmount/remount between views
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Onboarding mode - set when user explicitly wants to create or import from lock screen
  const [onboardingMode, setOnboardingMode] = useState<"create" | "import" | null>(null);
  // The locked SATCHEL a lock-screen setup or restore would replace.
  const replacedWallet = useRef<string | null>(null);
  const [currentView, setCurrentView] = useState<AppView>(() =>
    isBuyMarketplaceRoute() ? "buy" : "wallet",
  );
  const [selectedSmartAsset, setSelectedSmartAsset] = useState<SmartAssetData | null>(null);
  const [assetView, setAssetView] = useState<"crypto" | "smart">("crypto");

  // Intro video hook (Electron packaged app only, first launch)
  const { showIntroVideo, handleIntroComplete } = useIntroVideo();

  const wallet = useSatchelWallet();

  // Chain configuration from onboarding (persisted in ~/.satchel/connection.jsonc or localStorage)
  const [enabledChains, setEnabledChains] = useState<ChainConfig[]>([]);

  // The alice/bob wallet-name heuristic is a demo convenience, honoured only in demo mode.
  const isDemoWallet =
    siteMetadata.demoMode.enabled &&
    (wallet.walletName ? ["alice", "bob"].includes(wallet.walletName.toLowerCase()) : false);

  const [bitcoinNetwork, setBitcoinNetwork] = useState<BitcoinNetwork>(BITCOIN_NETWORK);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        if (isElectron && window.electronAPI) {
          const connection = await window.electronAPI.getSatchelConnection();
          if (connection?.enabledChains) setEnabledChains(connection.enabledChains);
          if (connection?.bitcoinNetwork) setBitcoinNetwork(connection.bitcoinNetwork);
        } else {
          const storedChains = localStorage.getItem("satchel_chain_config");
          if (storedChains) setEnabledChains(JSON.parse(storedChains));
          const storedNetwork = localStorage.getItem("satchel_bitcoin_network");
          if (storedNetwork) setBitcoinNetwork(storedNetwork as BitcoinNetwork);
        }
      } catch (error) {
        console.error("Failed to load config:", error);
      }
    };

    if (hasCompletedOnboarding) {
      loadConfig();
    }
  }, [hasCompletedOnboarding]);

  // Demo wallets have no real funds, so they never run on mainnet.
  useEffect(() => {
    if (isDemoWallet && bitcoinNetwork === "mainnet") {
      setBitcoinNetwork("regtest");
    }
  }, [isDemoWallet, bitcoinNetwork]);

  const handleBitcoinNetworkChange = async (network: BitcoinNetwork) => {
    setBitcoinNetwork(network);
    try {
      if (isElectron && window.electronAPI) {
        const connection = await window.electronAPI.getSatchelConnection();
        await window.electronAPI.setSatchelConnection({
          ...connection,
          connected: connection?.connected ?? false,
          address: connection?.address ?? "",
          bitcoinNetwork: network,
        });
      } else {
        localStorage.setItem("satchel_bitcoin_network", network);
      }
    } catch (error) {
      console.error("Failed to save network selection:", error);
    }
  };

  // The desktop app's menu can open Settings.
  useEffect(() => {
    if (isElectron && window.electronAPI?.onNavigate) {
      const unsubscribe = window.electronAPI.onNavigate((path: string) => {
        if (path === "/settings") {
          setCurrentView("settings");
        }
      });
      return () => unsubscribe();
    }
  }, []);

  // Core dashboard Buy actions push /satchel/buy and dispatch popstate.
  useEffect(() => {
    const syncViewFromPath = () => {
      const { pathname } = window.location;
      if (isBuyMarketplaceRoute()) {
        setAssetView("smart");
        setCurrentView("buy");
        return;
      }

      if (pathname === "/" || pathname === "/satchel") {
        setCurrentView((view) => (view === "buy" ? "designer" : view));
      }
    };

    syncViewFromPath();
    window.addEventListener("popstate", syncViewFromPath);
    window.addEventListener("hashchange", syncViewFromPath);
    return () => {
      window.removeEventListener("popstate", syncViewFromPath);
      window.removeEventListener("hashchange", syncViewFromPath);
    };
  }, []);

  // A desktop reset: the main process clears its files and reloads the page; the
  // page clears its own storage first.
  useEffect(() => {
    if (isElectron && window.electronAPI?.onResetSatchel) {
      const unsubscribe = window.electronAPI.onResetSatchel(() => {
        localStorage.removeItem("satchel_onboarding_completed");
        localStorage.removeItem("satchel_wallet_name");
        localStorage.removeItem("satchel_chain_config");
        localStorage.removeItem("satchel_bitcoin_network");
        sessionStorage.removeItem(WALLET_STORAGE_KEYS.PASSWORD);
      });
      return () => unsubscribe();
    }
  }, []);

  const handleOnboardingComplete = () => {
    localStorage.setItem("satchel_onboarding_completed", "true");
    setIsTransitioning(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setHasCompletedOnboarding(true);
        setCurrentView("wallet");
        setIsTransitioning(false);
      });
    });
  };

  const handleOpenSmartAsset = (assetId: string) => {
    const primitiveAsset = getMockSmartAssets().find((a) => a.id === assetId);
    if (primitiveAsset) {
      setSelectedSmartAsset(primitiveAsset);
      setCurrentView("smart-asset");
      return;
    }

    const composite = getComposites().find((c) => c.id === assetId);
    if (composite) {
      setSelectedSmartAsset({
        id: composite.id,
        name: composite.name,
        ticker: composite.name.substring(0, 3).toUpperCase(),
        description: `Composite Smart Asset created ${new Date(composite.created_at).toLocaleDateString()}. Contains ${composite.components.length} components with ${composite.composition_links.length} connections.`,
        category: "composite",
        status: "active",
        governanceEnabled: false,
      });
      setCurrentView("smart-asset");
    }
  };

  // Opens the Smart Assets marketplace in the user's browser. Electron main
  // redirects HTTPS pop-outs to the default browser.
  const handleOpenSmartAssetsMarketplace = () => {
    const marketplaceUrl = siteMetadata.urls.smartAssets.marketplace;
    const popup = window.open(
      marketplaceUrl,
      "satchel-smart-assets-marketplace",
      "popup=yes,width=1280,height=900,resizable=yes,scrollbars=yes",
    );

    if (popup) {
      popup.focus();
      return;
    }

    if (isElectron && window.electronAPI?.openExternalUrl) {
      void window.electronAPI.openExternalUrl(marketplaceUrl);
      return;
    }

    window.location.href = marketplaceUrl;
  };

  const handleBuyMarketplaceReturn = () => {
    if (window.opener && !window.opener.closed) {
      window.close();
      return;
    }

    window.history.pushState({}, "", "/satchel");
    setAssetView("smart");
    setCurrentView("designer");
  };

  // Unlocking opens on Assets.
  const handleLock = () => {
    setCurrentView("wallet");
    wallet.lock();
  };

  // Clears the local wallet reference and restarts onboarding. The desktop app
  // also holds files and the recovery-phrase vault, which its main process clears.
  const handleReset = async () => {
    if (wallet.walletName && wallet.walletPassword) {
      await deleteServerWallets({
        satchelApi: wallet.api,
        rgbLibApi: new RgbLibApiClient(),
        walletName: wallet.walletName,
        password: wallet.walletPassword,
        network: wallet.network,
      });
    }
    if (isElectron && window.electronAPI) {
      void window.electronAPI.resetSatchel();
      return;
    }
    wallet.clearWallet();
    localStorage.removeItem("satchel_onboarding_completed");
    localStorage.removeItem("satchel_chain_config");
    sessionStorage.removeItem("satchel_onboarding_warning");
    sessionStorage.removeItem("satchel_dashboard_warning");
    setIsTransitioning(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setHasCompletedOnboarding(false);
        setIsTransitioning(false);
      });
    });
  };

  if (showIntroVideo) {
    return <StartupVideo onComplete={handleIntroComplete} />;
  }

  if (isTransitioning) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sa-bg">
        <div className="animate-pulse">
          <img src={ASSET_PATHS.SATCHEL_LOGO} alt="" className="h-24 w-24" />
        </div>
      </div>
    );
  }

  // Wallet cleared because it no longer exists on the backend.
  const isWalletCleared = hasCompletedOnboarding && !wallet.walletName && !wallet.isLoading;

  if (!hasCompletedOnboarding || isWalletCleared || onboardingMode) {
    return (
      <Suspense fallback={<ViewLoader />}>
        <OnboardingPage
          onComplete={() => {
            // The locked SATCHEL is replaced only now, so its stored recovery
            // phrase goes now too.
            const replaced = replacedWallet.current;
            replacedWallet.current = null;
            if (replaced) void window.electronAPI?.walletSeed?.remove(replaced);
            setOnboardingMode(null);
            handleOnboardingComplete();
          }}
          onCancel={
            onboardingMode
              ? () => {
                  replacedWallet.current = null;
                  setOnboardingMode(null);
                }
              : undefined
          }
          initialAction={onboardingMode}
        />
      </Suspense>
    );
  }

  const leaveLockScreen = (mode: "create" | "import") => {
    replacedWallet.current = wallet.walletName;
    setOnboardingMode(mode);
  };

  const lockScreen = (
    <WalletLockScreen
      onRecoverWithPhrase={() => leaveLockScreen("import")}
      onCreateNew={() => leaveLockScreen("create")}
    />
  );

  if (wallet.needsPasswordAuth) {
    return lockScreen;
  }

  // A stored wallet with no session password will need unlocking; show the lock
  // screen while loading rather than flash the app.
  if (
    wallet.isLoading &&
    localStorage.getItem(WALLET_STORAGE_KEYS.WALLET_NAME) &&
    !sessionStorage.getItem(WALLET_STORAGE_KEYS.PASSWORD)
  ) {
    return lockScreen;
  }

  // Marketplace pop-out route renders without the app frame.
  if (currentView === "buy") {
    return (
      <Suspense fallback={<ViewLoader />}>
        <BuyMarketplacePage onBack={handleBuyMarketplaceReturn} />
      </Suspense>
    );
  }

  return (
    <AppShell
      groups={navigationGroups(siteMetadata.prototypes.enabled)}
      currentView={navigationViewFor(currentView)}
      onNavigate={setCurrentView}
      status={<NetworkStatus />}
    >
      <Suspense fallback={<ViewLoader />}>
        {currentView === "wallet" ? (
          <WalletHome />
        ) : currentView === "satchel" ? (
          <SmartAssetsHome />
        ) : currentView === "settings" ? (
          <SettingsPage onLock={handleLock} onReset={handleReset} />
        ) : currentView === "exchange" ? (
          <ExchangeView />
        ) : currentView === "credits" ? (
          <Credits onBack={() => setCurrentView("wallet")} />
        ) : currentView === "smart-asset" && selectedSmartAsset ? (
          <SmartAssetMasterContainer
            asset={selectedSmartAsset}
            onBack={() => {
              setSelectedSmartAsset(null);
              setCurrentView("designer");
            }}
            onOpenScheduling={() => setCurrentView("scheduling")}
          />
        ) : currentView === "scheduling" ? (
          <SchedulingPage onBack={() => setCurrentView("designer")} />
        ) : (
          <Dashboard
            assetView={assetView}
            onAssetViewChange={setAssetView}
            enabledChains={enabledChains}
            bitcoinNetwork={bitcoinNetwork}
            onBitcoinNetworkChange={handleBitcoinNetworkChange}
            demoModeEnabled={isDemoWallet}
            showDemoToggle={siteMetadata.demoMode.showToggle}
            showContactsPanel={false}
            onOpenSmartAsset={handleOpenSmartAsset}
            onOpenScheduling={() => setCurrentView("scheduling")}
            onNavigateToCredits={() => setCurrentView("credits")}
            onOpenSmartAssetsMarketplace={handleOpenSmartAssetsMarketplace}
          />
        )}
      </Suspense>
    </AppShell>
  );
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ServerConnectionProvider>
      <ServerConnectionGate>
        <SatchelWalletProvider>
          <App />
        </SatchelWalletProvider>
      </ServerConnectionGate>
    </ServerConnectionProvider>
  </React.StrictMode>,
);
